/**
 * SpreadsheetSheet.js — Autonomous spreadsheet grid (T-049).
 *
 * A dependency-free editable grid backed by the pure `spreadsheetEngine`.
 * Fully driven by props; it does NOT know about EditorPage or persistence — it
 * just calls `onUpdate(newContent)` (debounced ~600ms) with the full JSON.
 *
 * Props:
 *   - content: { type:'spreadsheet', rows, cols, cells:{A1:'…'}, colWidths?,
 *              rowHeights?, wrapCols? }
 *              (empty/null -> starts blank at DEFAULT_ROWS x DEFAULT_COLS).
 *   - onUpdate(newContent): called (debounced) with the complete JSON on edits.
 *   - readOnly?: boolean — disables editing and the row/col controls.
 *   - landscape?: boolean — only widens the visible area.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { evaluateSheet, formatValue, _internals } from './spreadsheetEngine';
import { rangeRefString, cellsInRange } from './spreadsheetSelection';

const { numToCol } = _internals;

// Excel/Sheets-like rotating palette for cells referenced by the formula being
// edited. Each distinct ref/range gets the next color; repeats reuse it.
const REF_COLORS = ['#1a73e8', '#188038', '#a142f4', '#e37400', '#12a4af', '#d93025'];

// Functions supported by the engine (see spreadsheetEngine.js `callFunction`).
// Drives the #9 autocomplete dropdown.
const FUNCTIONS = [
  { name: 'SUM', sig: 'SUM(rango)', desc: 'Suma' },
  { name: 'AVERAGE', sig: 'AVERAGE(rango)', desc: 'Promedio' },
  { name: 'AVG', sig: 'AVG(rango)', desc: 'Promedio' },
  { name: 'MIN', sig: 'MIN(rango)', desc: 'Mínimo' },
  { name: 'MAX', sig: 'MAX(rango)', desc: 'Máximo' },
  { name: 'COUNT', sig: 'COUNT(rango)', desc: 'Cuenta números' },
  { name: 'PRODUCT', sig: 'PRODUCT(rango)', desc: 'Producto' },
  { name: 'IF', sig: 'IF(cond; v1; v2)', desc: 'Condicional' },
];

// Shared offscreen canvas for column auto-fit text measurement.
let _measureCanvas = null;
function measureText(text) {
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
  const ctx = _measureCanvas.getContext('2d');
  ctx.font = '14px ui-sans-serif, system-ui, -apple-system, sans-serif';
  return ctx.measureText(String(text)).width;
}

// Static input classes (hoisted so the memoized Cell needs no extra prop for them).
const INPUT_BASE =
  'w-full h-full px-1 py-0.5 text-sm text-slate-900 ' +
  'dark:text-slate-100 border-0 outline-none focus:ring-2 focus:ring-blue-400 ' +
  'focus:ring-inset';

// Memoized grid cell. All props are primitives plus two STABLE refs/callbacks
// (handlersRef, registerRef), so React.memo skips re-rendering every cell on each
// keystroke — only cells whose value/selection/edit/ref state changed re-render.
// The handlers live behind `handlersRef` (updated each parent render) so their
// changing closures never invalidate the memo; the cell key travels via data-k.
const Cell = React.memo(function Cell({
  cellKey, value, isSelected, isEditing, isErr, inRange, refColor, wrapped, colWidth, readOnly, handlersRef, registerRef,
}) {
  const onFocus = useCallback((e) => handlersRef.current.onFocus(e), [handlersRef]);
  const onBlur = useCallback((e) => handlersRef.current.onBlur(e), [handlersRef]);
  const onChange = useCallback((e) => handlersRef.current.onChange(e), [handlersRef]);
  const onKeyDown = useCallback((e) => handlersRef.current.onKeyDown(e), [handlersRef]);
  const onMouseDown = useCallback((e) => handlersRef.current.onMouseDown(e), [handlersRef]);
  const onMouseEnter = useCallback((e) => handlersRef.current.onMouseEnter(e), [handlersRef]);
  const setRef = useCallback((el) => registerRef(cellKey, el), [cellKey, registerRef]);

  const bgClass = inRange ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-white dark:bg-slate-800';
  const fieldClass =
    INPUT_BASE + ' ' + (refColor ? '' : bgClass) +
    (isErr && !isEditing ? ' text-red-600 dark:text-red-400' : '') +
    (wrapped ? ' resize-none' : '');
  const fieldStyle = {
    ...(refColor ? { backgroundColor: `${refColor}1a`, cursor: 'move' } : {}),
    ...(wrapped ? { whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' } : {}),
  };
  const commonProps = {
    ref: setRef,
    value,
    'data-k': cellKey,
    onFocus,
    onBlur,
    onChange,
    onKeyDown,
    readOnly,
    className: fieldClass,
    style: Object.keys(fieldStyle).length ? fieldStyle : undefined,
  };
  return (
    <td
      data-k={cellKey}
      className={
        'border p-0 ' +
        (isSelected ? 'border-blue-400 dark:border-blue-500' : 'border-slate-200 dark:border-slate-700')
      }
      style={{
        minWidth: '6rem',
        ...(colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px` } : {}),
        ...(wrapped ? { verticalAlign: 'top' } : {}),
        ...(refColor ? { boxShadow: `inset 0 0 0 2px ${refColor}` } : {}),
      }}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      {wrapped
        ? <textarea rows={1} {...commonProps} />
        : <input type="text" {...commonProps} />}
    </td>
  );
});

/**
 * Scan a formula string for cell references and ranges WITH their character
 * positions (so #3 can rewrite the exact token when a colored cell is dragged).
 * Returns [{ keys:[...], start, end, a:{col,row}, b:{col,row}, isRange }].
 * Function names (letters only, no digits) never match parseRef, so they're
 * skipped. ponytail: a quoted "A1" inside a string literal would be picked up as
 * a ref (rare in these formulas); acceptable ceiling.
 */
function parseFormulaRefs(formula) {
  const refs = [];
  if (!formula) return refs;
  const re = /(\$?[A-Za-z]+\$?\d+)(?::(\$?[A-Za-z]+\$?\d+))?/g;
  let m;
  while ((m = re.exec(formula)) !== null) {
    const r1 = _internals.parseRef(m[1]);
    if (!r1) continue;
    // Refs fuera de cualquier grid posible (fila/columna > máximos) no se
    // colorean: son texto inválido, y expandirlos costaba memoria (T-080).
    if (r1.row > MAX_ROWS || r1.col > MAX_COLS) continue;
    let keys = [r1.key];
    let a = r1;
    let b = r1;
    let isRange = false;
    if (m[2]) {
      const r2 = _internals.parseRef(m[2]);
      if (r2) { keys = cellsInRange(r1.key, r2.key); b = r2; isRange = true; }
    }
    refs.push({ keys, start: m.index, end: m.index + m[0].length, a, b, isRange });
  }
  return refs;
}

/**
 * Excel-style keyboard navigation: given a cell key and a (dRow,dCol) step,
 * return the destination cell key clamped to the 1..rows / 1..cols grid. Pure
 * (no React) so the boundary/clamp logic is unit-testable in isolation.
 */
export function nextCell(key, dRow, dCol, rows, cols) {
  const ref = _internals.parseRef(key);
  if (!ref) return key;
  const nr = Math.min(rows, Math.max(1, ref.row + dRow));
  const nc = Math.min(cols, Math.max(1, ref.col + dCol));
  return numToCol(nc) + nr;
}

const DEFAULT_ROWS = 20;
const DEFAULT_COLS = 8;
const MAX_ROWS = 200;
const MAX_COLS = 52;
const DEBOUNCE_MS = 600;
const DEFAULT_COL_WIDTH = 96; // px, matches the '6rem' min-width fallback
const DEFAULT_ROW_HEIGHT = 30; // px
const MIN_COL_WIDTH = 40;
const MIN_ROW_HEIGHT = 20;

/** Normalize incoming content prop into { rows, cols, cells, colWidths, rowHeights, wrapCols }. */
function normalize(content) {
  const c = content && typeof content === 'object' ? content : {};
  const rows = Math.min(MAX_ROWS, Math.max(1, parseInt(c.rows, 10) || DEFAULT_ROWS));
  const cols = Math.min(MAX_COLS, Math.max(1, parseInt(c.cols, 10) || DEFAULT_COLS));
  const cells = c.cells && typeof c.cells === 'object' ? { ...c.cells } : {};
  const colWidths = c.colWidths && typeof c.colWidths === 'object' ? { ...c.colWidths } : {};
  const rowHeights = c.rowHeights && typeof c.rowHeights === 'object' ? { ...c.rowHeights } : {};
  // wrapCols persisted as either an array of letters or a map letter->true.
  let wrapCols = {};
  if (Array.isArray(c.wrapCols)) c.wrapCols.forEach((l) => { if (l) wrapCols[l] = true; });
  else if (c.wrapCols && typeof c.wrapCols === 'object') wrapCols = { ...c.wrapCols };
  return { rows, cols, cells, colWidths, rowHeights, wrapCols };
}

export default function SpreadsheetSheet({ content, onUpdate, readOnly = false, landscape = false }) {
  // Intentionally read `content` only on mount (component is remounted via key
  // on sheet change), so the empty dep array is correct here.
  const initial = useMemo(() => normalize(content), []);
  const [rows, setRows] = useState(initial.rows);
  const [cols, setCols] = useState(initial.cols);
  const [cells, setCells] = useState(initial.cells);
  const [colWidths, setColWidths] = useState(initial.colWidths);
  const [rowHeights, setRowHeights] = useState(initial.rowHeights);
  const [wrapCols, setWrapCols] = useState(initial.wrapCols);

  const [activeCell, setActiveCell] = useState(null); // selected cell, e.g. 'A1'
  const [editingKey, setEditingKey] = useState(null); // cell whose input is focused
  const [editingValue, setEditingValue] = useState('');
  const [range, setRange] = useState(null); // {anchor,focus}|null — mouse-drag selection rectangle
  const [ac, setAc] = useState(null); // function-autocomplete dropdown state | null
  const [barFocused, setBarFocused] = useState(false); // formula bar has focus (drives ref coloring)

  const draggingRef = useRef(false); // left mouse button down, dragging over cells
  const pointModeDragRef = useRef(false); // true if the current drag is a formula ref-pick
  const rangeAnchorRef = useRef(null);
  const rangeFocusRef = useRef(null);
  const activeInputRef = useRef(null); // last-focused cell/formula-bar <input>, for caret math
  const cellInputsRef = useRef({}); // cellKey -> its <input>/<textarea> el, for keyboard nav focus
  const cellHandlersRef = useRef({}); // updated each render → memoized Cell always calls the latest handlers
  const resizeRef = useRef(null); // in-flight row/col resize drag state
  // Excel-like point-mode: the last ref inserted by a cell-click, so the NEXT
  // click REPLACES it (until the user types an operator). null once broken.
  const lastRefRef = useRef(null); // { start, len, text } | null
  // #3: in-flight drag of a COLORED formula ref (moving the reference).
  const refDragRef = useRef(null); // { start, len, minC, maxC, minR, maxR, isRange, grabCol, grabRow } | null

  const debounceRef = useRef(null);
  const cancelCommitRef = useRef(false); // set by Escape to discard the pending edit
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Recompute every value whenever the raw cells / bounds change.
  const values = useMemo(() => evaluateSheet(cells, rows, cols).values, [cells, rows, cols]);

  const scheduleUpdate = useCallback((nextCells, nextRows, nextCols, nextColWidths, nextRowHeights, nextWrapCols) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (typeof onUpdateRef.current === 'function') {
        onUpdateRef.current({
          type: 'spreadsheet',
          rows: nextRows,
          cols: nextCols,
          cells: nextCells,
          colWidths: nextColWidths,
          rowHeights: nextRowHeights,
          wrapCols: nextWrapCols,
        });
      }
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const commitCell = useCallback((key, rawValue) => {
    setCells((prev) => {
      const next = { ...prev };
      // Blank (empty or whitespace-only) → drop the cell, Excel-style. Non-blank
      // keeps the raw value verbatim (intentional leading/trailing spaces stay).
      if (rawValue == null || String(rawValue).trim() === '') {
        delete next[key];
      } else {
        next[key] = rawValue;
      }
      scheduleUpdate(next, rows, cols, colWidths, rowHeights, wrapCols);
      return next;
    });
  }, [rows, cols, colWidths, rowHeights, wrapCols, scheduleUpdate]);

  // Excel-style keyboard nav: select `key`, focus its input, and (after the
  // controlled value settles) select-all so the next printable char OVERWRITES
  // it (type-to-overwrite) while F2/click still place a caret. Focusing the new
  // input auto-commits the previous cell via its onBlur.
  const focusCell = useCallback((key) => {
    setActiveCell(key);
    rangeAnchorRef.current = key;
    rangeFocusRef.current = key;
    setRange({ anchor: key, focus: key });
    const el = cellInputsRef.current[key];
    if (el) {
      el.focus();
      requestAnimationFrame(() => {
        const e2 = cellInputsRef.current[key];
        if (e2 && document.activeElement === e2 && typeof e2.select === 'function') e2.select();
      });
    }
  }, []);

  // Clear the contents of the given cell keys (Delete/Backspace over a
  // selection). Persists once if anything actually changed.
  const clearCells = useCallback((keys) => {
    setCells((prev) => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((k) => { if (k in next) { delete next[k]; changed = true; } });
      if (changed) scheduleUpdate(next, rows, cols, colWidths, rowHeights, wrapCols);
      return changed ? next : prev;
    });
  }, [rows, cols, colWidths, rowHeights, wrapCols, scheduleUpdate]);

  const handleFocus = useCallback((key) => {
    setActiveCell(key);
    setEditingKey(key);
    setEditingValue(cells[key] !== undefined ? cells[key] : '');
    setAc(null);
    lastRefRef.current = null;
  }, [cells]);

  const handleBlur = useCallback(() => {
    // Commit but KEEP the cell selected so the formula bar stays usable.
    if (cancelCommitRef.current) {
      cancelCommitRef.current = false; // Escape discarded the edit
      // Revert the in-progress text to the committed cell value so the cell
      // RENDERS (not the raw formula) and the formula bar shows the real value.
      if (editingKey !== null) {
        setEditingValue(cells[editingKey] !== undefined ? cells[editingKey] : '');
      }
    } else if (editingKey !== null) {
      commitCell(editingKey, editingValue);
    }
    setEditingKey(null);
    setAc(null);
    lastRefRef.current = null;
    // editingValue MUST be a dep: without it this callback keeps a stale value
    // captured at focus time and commits the OLD text on Enter/blur.
  }, [editingKey, editingValue, cells, commitCell]);

  // Stable ref registrar for cell inputs (was an inline closure per cell/render).
  const registerInput = useCallback((key, el) => {
    if (el) cellInputsRef.current[key] = el;
    else delete cellInputsRef.current[key];
  }, []);

  // --- #9 function autocomplete --------------------------------------------

  // Compute dropdown state from the current value + caret. Returns null (closed)
  // unless the value is a formula whose trailing letter-token prefixes a fn.
  const computeAc = useCallback((value, caret, inputEl) => {
    if (readOnly || !value || !value.trim().startsWith('=')) return null;
    const pos = typeof caret === 'number' ? caret : value.length;
    const before = value.slice(0, pos);
    const m = /([A-Za-z]+)$/.exec(before);
    if (!m) return null;
    const token = m[1].toUpperCase();
    const items = FUNCTIONS.filter((f) => f.name.startsWith(token));
    if (items.length === 0) return null;
    let rect = null;
    if (inputEl && typeof inputEl.getBoundingClientRect === 'function') {
      const r = inputEl.getBoundingClientRect();
      rect = { left: r.left, top: r.bottom, width: r.width };
    }
    return { open: true, items, index: 0, start: pos - token.length, end: pos, rect };
  }, [readOnly]);

  const selectAc = useCallback((item, acObj) => {
    const input = activeInputRef.current;
    setEditingValue((prev) => {
      const insert = item.name + '(';
      const next = prev.slice(0, acObj.start) + insert + prev.slice(acObj.end);
      const caretPos = acObj.start + insert.length;
      // Wait a tick so the controlled input re-renders before we set the caret.
      requestAnimationFrame(() => {
        if (input) {
          input.focus();
          input.setSelectionRange(caretPos, caretPos);
        }
      });
      return next;
    });
    setAc(null);
  }, []);

  // Intercept keys while the dropdown is open. Returns true if handled.
  const handleAcKeyDown = useCallback((e) => {
    if (!ac || !ac.open) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAc((a) => (a ? { ...a, index: (a.index + 1) % a.items.length } : a));
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAc((a) => (a ? { ...a, index: (a.index - 1 + a.items.length) % a.items.length } : a));
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      selectAc(ac.items[ac.index], ac);
      return true;
    }
    // Escape is NOT consumed here — it must fall through so it cancels the edit
    // (Excel-like). The dropdown closes anyway via setAc(null) on blur.
    return false;
  }, [ac, selectAc]);

  const handleCellChange = useCallback((e) => {
    const v = e.target.value;
    setEditingValue(v);
    setAc(computeAc(v, e.target.selectionStart, e.target));
    lastRefRef.current = null; // typing breaks the Excel point-mode replace chain
  }, [computeAc]);

  const handleKeyDown = useCallback((e) => {
    if (handleAcKeyDown(e)) return;
    // Read the live text (never the possibly-stale `editingValue` state) so this
    // callback needs no `editingValue` dep and stays fresh.
    const v = e.target.value;
    const isFormula = v.trim().startsWith('=');
    // Commit the current edit, then move the selection. Moving to a NEW cell
    // commits automatically via the old input's blur; a clamped move (already at
    // the edge) has no focus change, so we must commit in place explicitly.
    const commitAndMove = (dRow, dCol) => {
      if (!activeCell) return;
      const target = nextCell(activeCell, dRow, dCol, rows, cols);
      if (target === activeCell) { commitCell(activeCell, v); return; }
      focusCell(target);
    };
    if (e.key === 'Enter') {
      e.preventDefault();
      commitAndMove(e.shiftKey ? -1 : 1, 0); // Excel: Enter=down, Shift+Enter=up
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commitAndMove(0, e.shiftKey ? -1 : 1); // Tab=right, Shift+Tab=left
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // ponytail: if the text is a formula (starts with '='), leave arrows NATIVE
      // so point-mode ref-picking and in-formula caret editing keep working. For
      // plain values, arrows navigate between cells (Excel) — the ceiling is you
      // can't move the caret inside plain text with arrows; use F2/click/Home/End.
      if (isFormula) return;
      e.preventDefault();
      if (e.key === 'ArrowUp') commitAndMove(-1, 0);
      else if (e.key === 'ArrowDown') commitAndMove(1, 0);
      else if (e.key === 'ArrowLeft') commitAndMove(0, -1);
      else commitAndMove(0, 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelCommitRef.current = true; // handleBlur will skip the commit
      e.target.blur();
    }
  }, [handleAcKeyDown, activeCell, rows, cols, commitCell, focusCell]);

  // --- range selection (drag) + formula point-mode ref insertion -----------

  const rangeKeys = useMemo(
    () => (range && range.anchor !== range.focus ? new Set(cellsInRange(range.anchor, range.focus)) : null),
    [range],
  );

  // Excel-like cell coloring: while a formula is being edited, map each cell it
  // references to a palette color. The cell being edited (activeCell) is never
  // colored — it can't reference itself.
  const formulaRefInfo = useMemo(() => {
    const colors = new Map();       // cellKey -> color
    const cellRef = new Map();      // cellKey -> the ref object it belongs to (#3 drag)
    // Only while ACTIVELY editing a formula (cell input or formula bar focused).
    // Otherwise (e.g. after Esc/commit) the stale editingValue must not keep the
    // referenced cells colored.
    const editingFormula = (editingKey !== null || barFocused) && editingValue && editingValue.trim().startsWith('=');
    if (!editingFormula) return { colors, cellRef };
    let ci = 0;
    const bySig = new Map();
    for (const ref of parseFormulaRefs(editingValue)) {
      const sig = ref.keys.join(',');
      let color = bySig.get(sig);
      if (!color) { color = REF_COLORS[ci % REF_COLORS.length]; ci++; bySig.set(sig, color); }
      for (const k of ref.keys) {
        if (!colors.has(k)) { colors.set(k, color); cellRef.set(k, ref); }
      }
    }
    if (activeCell) { colors.delete(activeCell); cellRef.delete(activeCell); }
    return { colors, cellRef };
  }, [editingValue, activeCell, editingKey, barFocused]);
  const formulaRefColors = formulaRefInfo.colors;
  const cellRefMap = formulaRefInfo.cellRef;

  // #3: move the dragged colored ref by the drag delta, clamped to keep the whole
  // ref inside the grid; rewrites its exact token in the formula, live.
  const applyRefMove = useCallback((targetKey) => {
    const rd = refDragRef.current;
    if (!rd) return;
    const t = _internals.parseRef(targetKey);
    if (!t) return;
    let dc = t.col - rd.grabCol;
    let dr = t.row - rd.grabRow;
    dc = Math.max(1 - rd.minC, Math.min(cols - rd.maxC, dc));
    dr = Math.max(1 - rd.minR, Math.min(rows - rd.maxR, dr));
    if (dc === 0 && dr === 0) return;
    rd.minC += dc; rd.maxC += dc; rd.minR += dr; rd.maxR += dr;
    rd.grabCol += dc; rd.grabRow += dr;
    const newText = rd.isRange
      ? `${numToCol(rd.minC)}${rd.minR}:${numToCol(rd.maxC)}${rd.maxR}`
      : `${numToCol(rd.minC)}${rd.minR}`;
    const input = activeInputRef.current;
    const caret = rd.start + newText.length;
    setEditingValue((prev) => prev.slice(0, rd.start) + newText + prev.slice(rd.start + rd.len));
    rd.len = newText.length;
    requestAnimationFrame(() => {
      if (input) { input.focus(); input.setSelectionRange(caret, caret); }
    });
  }, [cols, rows]);

  // #2: publish the selected cell RANGE to the chat as 'selection' context so
  // "pregunta sobre las celdas seleccionadas" sends those values. Whole-sheet
  // context still comes from the chat's full/sheets modes. Collapsing the
  // selection clears it.
  useEffect(() => {
    if (readOnly) return;
    const emit = (text) => window.dispatchEvent(new CustomEvent('textSelected', {
      detail: { text, images: [], html: '', hasTables: false },
    }));
    if (!range || range.anchor === range.focus) { emit(''); return; }
    const a = _internals.parseRef(range.anchor);
    const f = _internals.parseRef(range.focus);
    if (!a || !f) { emit(''); return; }
    const c1 = Math.min(a.col, f.col), c2 = Math.max(a.col, f.col);
    const r1 = Math.min(a.row, f.row), r2 = Math.max(a.row, f.row);
    const lines = [];
    for (let r = r1; r <= r2; r++) {
      const rowVals = [];
      for (let c = c1; c <= c2; c++) rowVals.push(formatValue(values[numToCol(c) + r]));
      lines.push(rowVals.join('\t'));
    }
    emit(`Celdas seleccionadas (${rangeRefString(range.anchor, range.focus)}):\n${lines.join('\n')}`);
  }, [range, values, readOnly]);

  const insertRefAtCaret = useCallback((ref) => {
    const input = activeInputRef.current;
    setEditingValue((prev) => {
      let start = input && typeof input.selectionStart === 'number' ? input.selectionStart : prev.length;
      let end = input && typeof input.selectionEnd === 'number' ? input.selectionEnd : prev.length;
      // Excel: consecutive point-clicks (no operator typed in between) REPLACE
      // the previously-inserted ref instead of concatenating it.
      const pend = lastRefRef.current;
      if (pend && pend.start + pend.len === end && prev.slice(pend.start, pend.start + pend.len) === pend.text) {
        start = pend.start;
        end = pend.start + pend.len;
      }
      const next = prev.slice(0, start) + ref + prev.slice(end);
      const caretPos = start + ref.length;
      lastRefRef.current = { start, len: ref.length, text: ref };
      // Wait a tick so the controlled input re-renders with `next` before we
      // touch its selection range.
      requestAnimationFrame(() => {
        if (input) {
          input.focus();
          input.setSelectionRange(caretPos, caretPos);
        }
      });
      return next;
    });
    setAc(null); // a picked ref turns the trailing token into digits — drop stale fn list
  }, []);

  const handleCellMouseDown = useCallback((key) => (e) => {
    if (e.button !== 0 || readOnly) return;
    // #3: if editing a formula and you grab a COLORED ref cell, start moving that
    // reference (Excel drag-to-adjust) instead of inserting a new one.
    const editingFormula = activeInputRef.current !== null
      && document.activeElement === activeInputRef.current
      && editingValue.trim().startsWith('=');
    if (editingFormula) {
      const ref = cellRefMap.get(key);
      if (ref) {
        e.preventDefault();
        const gk = _internals.parseRef(key);
        refDragRef.current = {
          start: ref.start,
          len: ref.end - ref.start,
          isRange: ref.isRange,
          minC: Math.min(ref.a.col, ref.b.col),
          maxC: Math.max(ref.a.col, ref.b.col),
          minR: Math.min(ref.a.row, ref.b.row),
          maxR: Math.max(ref.a.row, ref.b.row),
          grabCol: gk.col,
          grabRow: gk.row,
        };
        draggingRef.current = true;
        pointModeDragRef.current = false;
        lastRefRef.current = null;
        return;
      }
    }
    // Point mode = the currently-focused input (cell OR formula bar — mousedown
    // fires before any focus change, so document.activeElement is still it) has
    // an in-progress formula. Deriving this from focus rather than `editingKey`
    // is what makes point mode work from the formula bar too (blurring the
    // cell input clears editingKey while you're still editing via the bar).
    // Clicking the very cell whose formula you're editing must NOT insert a
    // self-reference (circular). `activeCell` is that cell in BOTH edit routes
    // (in-cell keeps editingKey too; formula-bar clears editingKey but keeps
    // activeCell). When it's the self cell, skip point mode entirely and let the
    // click behave normally (caret placement in your own input).
    const editingSelf = key === activeCell;
    const pointMode = !editingSelf
      && activeInputRef.current !== null
      && document.activeElement === activeInputRef.current
      && editingValue.trim().startsWith('=');
    draggingRef.current = true;
    pointModeDragRef.current = pointMode;
    rangeAnchorRef.current = key;
    rangeFocusRef.current = key;
    setRange({ anchor: key, focus: key });
    if (pointMode) {
      // Keep focus/caret in the formula input being edited; don't let the
      // browser focus the clicked cell's own <input> (that would lose it and
      // would otherwise commit the in-progress formula as plain text).
      e.preventDefault();
    }
  }, [readOnly, editingValue, activeCell, cellRefMap]);

  const handleCellMouseEnter = useCallback((key) => (e) => {
    // #3: dragging a colored ref moves it live as you hover cells.
    if (refDragRef.current) {
      e.preventDefault();
      applyRefMove(key);
      return;
    }
    if (!draggingRef.current) return;
    // ponytail: preventDefault here only stops the browser default on this
    // event; it does not fully suppress native text-selection while dragging
    // across separate <input>s. If that flicker shows up, add
    // `user-select: none` on the table while draggingRef.current is true.
    e.preventDefault();
    rangeFocusRef.current = key;
    setRange({ anchor: rangeAnchorRef.current, focus: key });
    if (!pointModeDragRef.current && key !== rangeAnchorRef.current && editingKey !== null) {
      // Real drag (entered a 2nd cell): stop editing, becomes a pure highlight.
      if (activeInputRef.current) activeInputRef.current.blur();
    }
  }, [editingKey, applyRefMove]);

  useEffect(() => {
    const onDocMouseUp = () => {
      if (draggingRef.current && pointModeDragRef.current) {
        insertRefAtCaret(rangeRefString(rangeAnchorRef.current, rangeFocusRef.current));
      }
      refDragRef.current = null; // #3: end any ref-move drag
      draggingRef.current = false;
      pointModeDragRef.current = false;
    };
    document.addEventListener('mouseup', onDocMouseUp);
    return () => document.removeEventListener('mouseup', onDocMouseUp);
  }, [insertRefAtCaret]);

  // Ctrl/Cmd+C over a highlighted (non-editing) range -> TSV to clipboard.
  // ponytail: minimal nice-to-have, no paste/cut, no toast on failure.
  useEffect(() => {
    const onCopy = (e) => {
      // Only fire when nothing else on the page has focus (i.e. we just blurred
      // out of a drag-selection) so this never clobbers an unrelated Ctrl+C.
      if (editingKey !== null || !rangeKeys || !(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'c') return;
      if (document.activeElement && document.activeElement !== document.body) return;
      const a = _internals.parseRef(range.anchor);
      const f = _internals.parseRef(range.focus);
      const c1 = Math.min(a.col, f.col);
      const c2 = Math.max(a.col, f.col);
      const r1 = Math.min(a.row, f.row);
      const r2 = Math.max(a.row, f.row);
      const tsv = [];
      for (let r = r1; r <= r2; r++) {
        const rowVals = [];
        for (let c = c1; c <= c2; c++) rowVals.push(formatValue(values[numToCol(c) + r]));
        tsv.push(rowVals.join('\t'));
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(tsv.join('\n')).catch(() => {});
      }
    };
    document.addEventListener('keydown', onCopy);
    return () => document.removeEventListener('keydown', onCopy);
  }, [editingKey, range, rangeKeys, values]);

  // Keyboard when a cell/range is SELECTED but no input is focused (i.e. after a
  // header/drag selection): Delete/Backspace clears, arrows navigate, Enter/F2
  // edits, and a printable char starts editing with that char (type-to-overwrite).
  // Guarded to only fire when nothing on the page has focus, exactly like #copy.
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e) => {
      if (document.activeElement && document.activeElement !== document.body) return;
      if (!activeCell && !rangeKeys) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        clearCells(rangeKeys ? Array.from(rangeKeys) : [activeCell]);
        return;
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (!activeCell) return;
        e.preventDefault();
        const d = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[e.key];
        focusCell(nextCell(activeCell, d[0], d[1], rows, cols));
        return;
      }
      if (e.key === 'Enter' || e.key === 'F2') {
        if (!activeCell) return;
        e.preventDefault();
        focusCell(activeCell);
        return;
      }
      // Type-to-overwrite: focus the cell (enters edit mode via handleFocus, which
      // seeds editingValue with the cell content) then override with the typed
      // char. The later setState wins, so the cell starts fresh with that char.
      if (activeCell && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        const el = cellInputsRef.current[activeCell];
        if (el) el.focus();
        setEditingValue(e.key);
        requestAnimationFrame(() => {
          const e2 = cellInputsRef.current[activeCell];
          if (e2 && typeof e2.setSelectionRange === 'function') {
            const n = e2.value.length;
            e2.setSelectionRange(n, n);
          }
        });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [readOnly, activeCell, rangeKeys, rows, cols, focusCell, clearCells]);

  // --- #11 header selection (column / row / whole sheet) -------------------

  // True while a formula is being point-picked — header clicks must NOT fire
  // then (per spec). Non-formula edits fall through and get committed first.
  const inFormulaPointMode = useCallback(() => (
    activeInputRef.current !== null
    && document.activeElement === activeInputRef.current
    && editingValue.trim().startsWith('=')
  ), [editingValue]);

  const selectRange = useCallback((anchor, focus) => {
    if (inFormulaPointMode()) return; // don't disturb an in-progress formula
    // Commit any plain in-progress edit before we take over the selection.
    if (activeInputRef.current) activeInputRef.current.blur();
    rangeAnchorRef.current = anchor;
    rangeFocusRef.current = focus;
    setRange({ anchor, focus });
    setActiveCell(anchor);
    setEditingKey(null);
    setEditingValue(cells[anchor] !== undefined ? cells[anchor] : '');
    setAc(null);
  }, [cells, inFormulaPointMode]);

  const selectColumn = useCallback((letter) => selectRange(`${letter}1`, `${letter}${rows}`), [selectRange, rows]);
  const selectRow = useCallback((rowNum) => selectRange(`A${rowNum}`, `${numToCol(cols)}${rowNum}`), [selectRange, cols]);
  const selectAll = useCallback(() => selectRange('A1', `${numToCol(cols)}${rows}`), [selectRange, cols, rows]);

  // --- #8 resize (columns / rows) + auto-fit -------------------------------

  const startColResize = useCallback((letter) => (e) => {
    if (readOnly || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startW = colWidths[letter] || DEFAULT_COL_WIDTH;
    resizeRef.current = { kind: 'col', letter, startX: e.clientX, startW };
    const onMove = (ev) => {
      const st = resizeRef.current;
      if (!st) return;
      const w = Math.max(MIN_COL_WIDTH, Math.round(st.startW + (ev.clientX - st.startX)));
      setColWidths((prev) => ({ ...prev, [st.letter]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      resizeRef.current = null;
      setColWidths((prev) => { scheduleUpdate(cells, rows, cols, prev, rowHeights, wrapCols); return prev; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [readOnly, colWidths, cells, rows, cols, rowHeights, wrapCols, scheduleUpdate]);

  const startRowResize = useCallback((rowNum) => (e) => {
    if (readOnly || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startH = rowHeights[rowNum] || DEFAULT_ROW_HEIGHT;
    resizeRef.current = { kind: 'row', rowNum, startY: e.clientY, startH };
    const onMove = (ev) => {
      const st = resizeRef.current;
      if (!st) return;
      const h = Math.max(MIN_ROW_HEIGHT, Math.round(st.startH + (ev.clientY - st.startY)));
      setRowHeights((prev) => ({ ...prev, [st.rowNum]: h }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      resizeRef.current = null;
      setRowHeights((prev) => { scheduleUpdate(cells, rows, cols, colWidths, prev, wrapCols); return prev; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [readOnly, rowHeights, cells, rows, cols, colWidths, wrapCols, scheduleUpdate]);

  const autoFitCol = useCallback((letter) => (e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    let max = MIN_COL_WIDTH;
    for (let r = 1; r <= rows; r++) {
      const text = formatValue(values[`${letter}${r}`]);
      if (!text) continue;
      const w = measureText(text);
      if (w > max) max = w;
    }
    const width = Math.min(400, Math.round(max + 16)); // + horizontal padding
    setColWidths((prev) => {
      const next = { ...prev, [letter]: width };
      scheduleUpdate(cells, rows, cols, next, rowHeights, wrapCols);
      return next;
    });
  }, [readOnly, rows, values, cells, cols, rowHeights, wrapCols, scheduleUpdate]);

  const autoFitRow = useCallback((rowNum) => (e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    // ponytail: approximate — read the tallest rendered cell scrollHeight in the
    // row. Accurate only for the current column widths / wrap state.
    let max = DEFAULT_ROW_HEIGHT;
    const tr = e.currentTarget.closest('tr');
    if (tr) {
      tr.querySelectorAll('textarea, input').forEach((el) => {
        if (el.scrollHeight > max) max = el.scrollHeight;
      });
    }
    setRowHeights((prev) => {
      const next = { ...prev, [rowNum]: Math.min(300, max + 4) };
      scheduleUpdate(cells, rows, cols, colWidths, next, wrapCols);
      return next;
    });
  }, [readOnly, cells, rows, cols, colWidths, wrapCols, scheduleUpdate]);

  // --- #8 wrap text (per column of the active cell) ------------------------

  const activeColLetter = useMemo(() => {
    if (!activeCell) return null;
    const ref = _internals.parseRef(activeCell);
    return ref ? numToCol(ref.col) : null;
  }, [activeCell]);

  const toggleWrap = useCallback(() => {
    if (readOnly || !activeColLetter) return;
    setWrapCols((prev) => {
      const next = { ...prev };
      if (next[activeColLetter]) delete next[activeColLetter];
      else next[activeColLetter] = true;
      scheduleUpdate(cells, rows, cols, colWidths, rowHeights, next);
      return next;
    });
  }, [readOnly, activeColLetter, cells, rows, cols, colWidths, rowHeights, scheduleUpdate]);

  // --- structural controls -------------------------------------------------

  const addRow = useCallback(() => {
    if (readOnly) return;
    setRows((r) => {
      const nr = Math.min(MAX_ROWS, r + 1);
      scheduleUpdate(cells, nr, cols, colWidths, rowHeights, wrapCols);
      return nr;
    });
  }, [readOnly, cells, cols, colWidths, rowHeights, wrapCols, scheduleUpdate]);

  const removeRow = useCallback(() => {
    if (readOnly) return;
    setRows((r) => {
      if (r <= 1) return r;
      const nr = r - 1;
      // Drop cells that fall outside the new bounds.
      const next = {};
      Object.keys(cells).forEach((k) => {
        const ref = _internals.parseRef(k);
        if (ref && ref.row <= nr && ref.col <= cols) next[k] = cells[k];
      });
      // Prune row heights beyond the new bound.
      const nextRH = {};
      Object.keys(rowHeights).forEach((k) => { if (parseInt(k, 10) <= nr) nextRH[k] = rowHeights[k]; });
      setCells(next);
      setRowHeights(nextRH);
      scheduleUpdate(next, nr, cols, colWidths, nextRH, wrapCols);
      return nr;
    });
  }, [readOnly, cells, cols, colWidths, rowHeights, wrapCols, scheduleUpdate]);

  const addCol = useCallback(() => {
    if (readOnly) return;
    setCols((c) => {
      const nc = Math.min(MAX_COLS, c + 1);
      scheduleUpdate(cells, rows, nc, colWidths, rowHeights, wrapCols);
      return nc;
    });
  }, [readOnly, cells, rows, colWidths, rowHeights, wrapCols, scheduleUpdate]);

  const removeCol = useCallback(() => {
    if (readOnly) return;
    setCols((c) => {
      if (c <= 1) return c;
      const nc = c - 1;
      const next = {};
      Object.keys(cells).forEach((k) => {
        const ref = _internals.parseRef(k);
        if (ref && ref.col <= nc && ref.row <= rows) next[k] = cells[k];
      });
      // Prune col widths / wrap flags beyond the new bound.
      const keep = (letter) => {
        const rr = _internals.parseRef(`${letter}1`);
        return rr && rr.col <= nc;
      };
      const nextCW = {};
      Object.keys(colWidths).forEach((l) => { if (keep(l)) nextCW[l] = colWidths[l]; });
      const nextWrap = {};
      Object.keys(wrapCols).forEach((l) => { if (keep(l)) nextWrap[l] = wrapCols[l]; });
      setCells(next);
      setColWidths(nextCW);
      setWrapCols(nextWrap);
      scheduleUpdate(next, rows, nc, nextCW, rowHeights, nextWrap);
      return nc;
    });
  }, [readOnly, cells, rows, colWidths, rowHeights, wrapCols, scheduleUpdate]);

  // --- formula bar ---------------------------------------------------------

  const formulaBarValue = activeCell !== null
    ? editingValue
    : '';

  const handleFormulaBarChange = useCallback((e) => {
    const v = e.target.value;
    setEditingValue(v);
    setAc(computeAc(v, e.target.selectionStart, e.target));
    lastRefRef.current = null; // typing breaks the point-mode replace chain
  }, [computeAc]);

  const handleFormulaBarBlur = useCallback(() => {
    if (cancelCommitRef.current) {
      cancelCommitRef.current = false; // Escape discarded the edit
      if (activeCell !== null) {
        setEditingValue(cells[activeCell] !== undefined ? cells[activeCell] : '');
      }
    } else if (activeCell !== null) {
      commitCell(activeCell, editingValue);
    }
    setBarFocused(false);
    setAc(null);
    lastRefRef.current = null;
  }, [activeCell, editingValue, cells, commitCell]);

  const handleFormulaBarKeyDown = useCallback((e) => {
    if (handleAcKeyDown(e)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelCommitRef.current = true; // handleFormulaBarBlur will skip the commit
      e.target.blur();
    }
  }, [handleAcKeyDown]);

  // --- render --------------------------------------------------------------

  // Refresh the handler bag every render so the memoized Cell (which holds only
  // the stable ref) always invokes the latest closures. The cell key travels via
  // the event target's data-k, so these stay identity-stable to the Cell.
  cellHandlersRef.current = {
    onFocus: (e) => { handleFocus(e.target.dataset.k); activeInputRef.current = e.target; },
    onBlur: handleBlur,
    onChange: handleCellChange,
    onKeyDown: handleKeyDown,
    onMouseDown: (e) => handleCellMouseDown(e.currentTarget.dataset.k)(e),
    onMouseEnter: (e) => handleCellMouseEnter(e.currentTarget.dataset.k)(e),
  };

  const headerCellClass =
    'sticky top-0 z-10 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 ' +
    'text-xs font-medium text-center border border-slate-300 dark:border-slate-600 ' +
    'px-1 py-0.5 select-none';

  const rowHeaderClass =
    'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-medium ' +
    'text-center border border-slate-300 dark:border-slate-600 px-1 py-0.5 select-none';

  const colLetters = useMemo(
    () => Array.from({ length: cols }, (_, i) => numToCol(i + 1)),
    [cols],
  );

  const btnClass =
    'px-2 py-1 text-xs rounded border border-slate-300 dark:border-slate-600 ' +
    'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 ' +
    'hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 ' +
    'disabled:cursor-not-allowed';

  // ~5px grab handle on the right edge of a column header.
  const colHandleStyle = {
    position: 'absolute', top: 0, right: 0, width: '5px', height: '100%',
    cursor: 'col-resize', userSelect: 'none', zIndex: 20,
  };
  // ~5px grab handle on the bottom edge of a row header.
  const rowHandleStyle = {
    position: 'absolute', left: 0, bottom: 0, width: '100%', height: '5px',
    cursor: 'row-resize', userSelect: 'none', zIndex: 20,
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {/* Formula bar */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-slate-500 dark:text-slate-400 w-12 text-center select-none">
          {activeCell || ''}
        </span>
        <input
          type="text"
          className={
            'flex-1 px-2 py-1 text-sm font-mono rounded border border-slate-300 ' +
            'dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 ' +
            'dark:text-slate-100 outline-none focus:ring-2 focus:ring-blue-400'
          }
          placeholder="Selecciona una celda para editar su valor o fórmula"
          value={formulaBarValue}
          onChange={handleFormulaBarChange}
          onFocus={(e) => { activeInputRef.current = e.target; setBarFocused(true); }}
          onBlur={handleFormulaBarBlur}
          onKeyDown={handleFormulaBarKeyDown}
          disabled={readOnly || activeCell === null}
        />
      </div>

      {/* Toolbar */}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={btnClass} onClick={addRow} disabled={rows >= MAX_ROWS}>
            ＋ fila
          </button>
          <button type="button" className={btnClass} onClick={removeRow} disabled={rows <= 1}>
            － fila
          </button>
          <button type="button" className={btnClass} onClick={addCol} disabled={cols >= MAX_COLS}>
            ＋ columna
          </button>
          <button type="button" className={btnClass} onClick={removeCol} disabled={cols <= 1}>
            － columna
          </button>
          <button
            type="button"
            className={
              btnClass + (activeColLetter && wrapCols[activeColLetter] ? ' ring-2 ring-blue-400' : '')
            }
            onClick={toggleWrap}
            disabled={!activeColLetter}
            title="Ajustar texto en la columna de la celda activa"
          >
            Ajustar texto
          </button>
          <span className="text-xs text-slate-400 dark:text-slate-500 ml-2 select-none">
            {rows} × {cols}
          </span>
        </div>
      )}

      {/* Grid */}
      <div
        className="overflow-x-auto border border-slate-300 dark:border-slate-600 rounded"
        style={{ maxWidth: '100%' }}
      >
        <table className="border-collapse" style={{ minWidth: landscape ? '1100px' : 'auto' }}>
          <thead>
            <tr>
              <th
                className={headerCellClass + ' cursor-pointer'}
                style={{ minWidth: '2.5rem' }}
                onClick={selectAll}
                title="Seleccionar toda la hoja"
              />
              {colLetters.map((letter) => (
                <th
                  key={letter}
                  className={headerCellClass + ' cursor-pointer'}
                  style={{
                    position: 'relative',
                    minWidth: colWidths[letter] ? `${colWidths[letter]}px` : '6rem',
                    width: colWidths[letter] ? `${colWidths[letter]}px` : undefined,
                  }}
                  onClick={() => selectColumn(letter)}
                >
                  {letter}
                  {!readOnly && (
                    <div
                      style={colHandleStyle}
                      onMouseDown={startColResize(letter)}
                      onDoubleClick={autoFitCol(letter)}
                      onClick={(e) => e.stopPropagation()}
                      title="Arrastra para redimensionar · doble clic para ajustar"
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => {
              const rowNum = r + 1;
              const rowH = rowHeights[rowNum];
              return (
                <tr key={rowNum} style={rowH ? { height: `${rowH}px` } : undefined}>
                  <td
                    className={rowHeaderClass + ' cursor-pointer'}
                    style={{ position: 'relative' }}
                    onClick={() => selectRow(rowNum)}
                  >
                    {rowNum}
                    {!readOnly && (
                      <div
                        style={rowHandleStyle}
                        onMouseDown={startRowResize(rowNum)}
                        onDoubleClick={autoFitRow(rowNum)}
                        onClick={(e) => e.stopPropagation()}
                        title="Arrastra para redimensionar · doble clic para ajustar"
                      />
                    )}
                  </td>
                  {/* ponytail: wrapped columns use a <textarea> (see Cell) so editing
                      + caret math keep working. Ceilings: rows=1+overflow:hidden clips
                      wrapped lines past the first until the row is resized; text only
                      wraps once the column has a fixed colWidths[letter]. */}
                  {colLetters.map((letter) => {
                    const key = `${letter}${rowNum}`;
                    const computed = values[key];
                    return (
                      <Cell
                        key={key}
                        cellKey={key}
                        value={editingKey === key ? editingValue : formatValue(computed)}
                        isEditing={editingKey === key}
                        isSelected={activeCell === key}
                        isErr={typeof computed === 'string' && /^#[A-Z0-9/]+!$/.test(computed)}
                        inRange={rangeKeys ? rangeKeys.has(key) : false}
                        refColor={formulaRefColors.get(key) || null}
                        wrapped={!!wrapCols[letter]}
                        colWidth={colWidths[letter] || 0}
                        readOnly={readOnly}
                        handlersRef={cellHandlersRef}
                        registerRef={registerInput}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* #9 function autocomplete dropdown (anchored to the active input) */}
      {ac && ac.open && ac.rect && (
        <div
          style={{
            position: 'fixed',
            left: `${ac.rect.left}px`,
            top: `${ac.rect.top}px`,
            minWidth: `${Math.max(160, ac.rect.width)}px`,
            maxHeight: '11rem', // ~6 items
            overflowY: 'auto',
            zIndex: 9999,
          }}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg text-sm"
          // Keep focus in the input so the click selects without blurring.
          onMouseDown={(e) => e.preventDefault()}
        >
          {ac.items.map((f, i) => (
            <div
              key={f.name}
              className={
                'px-2 py-1 cursor-pointer flex justify-between gap-3 ' +
                (i === ac.index
                  ? 'bg-blue-100 dark:bg-blue-900/50'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700')
              }
              onMouseEnter={() => setAc((a) => (a ? { ...a, index: i } : a))}
              onClick={() => selectAc(f, ac)}
            >
              <span className="font-mono text-slate-800 dark:text-slate-100">{f.sig}</span>
              <span className="text-slate-400 dark:text-slate-500">{f.desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
